import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = {
  // Collections
  getCollections: async () => {
    const response = await axios.get(`${API}/collections`);
    return response.data;
  },

  getCollectionBySlug: async (slug) => {
    const response = await axios.get(`${API}/collections/${slug}`);
    return response.data;
  },

  // Products
  getProducts: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.collection) params.append('collection', filters.collection);
    if (filters.search) params.append('search', filters.search);
    if (filters.sort) params.append('sort', filters.sort);
    
    const response = await axios.get(`${API}/products?${params.toString()}`);
    return response.data;
  },

  getProduct: async (id) => {
    const response = await axios.get(`${API}/products/${id}`);
    return response.data;
  },

  // Orders
  createOrder: async (orderData) => {
    const response = await axios.post(`${API}/orders`, orderData);
    return response.data;
  },

  getOrdersByEmail: async (email) => {
    const response = await axios.get(`${API}/orders?email=${email}`);
    return response.data;
  },

  // Newsletter
  subscribeNewsletter: async (email) => {
    const response = await axios.post(`${API}/newsletter/subscribe`, { email });
    return response.data;
  },

  // Contact
  submitContact: async (messageData) => {
    const response = await axios.post(`${API}/contact`, messageData);
    return response.data;
  },

  // Admin - Auth
  adminLogin: async (password) => {
    const response = await axios.post(`${API}/admin/login`, { password });
    return response.data;
  },

  // Admin - Orders
  adminGetOrders: async (token) => {
    const response = await axios.get(`${API}/admin/orders`, {
      headers: { 'X-Admin-Token': token }
    });
    return response.data;
  },

  adminUpdateOrderStatus: async (orderId, status, token) => {
    const response = await axios.put(
      `${API}/admin/orders/${orderId}/status`,
      { status },
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // Admin - Products
  adminCreateProduct: async (productData, token) => {
    const response = await axios.post(
      `${API}/admin/products`,
      productData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  adminUpdateProduct: async (productId, productData, token) => {
    const response = await axios.put(
      `${API}/admin/products/${productId}`,
      productData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  adminDeleteProduct: async (productId, token) => {
    const response = await axios.delete(
      `${API}/admin/products/${productId}`,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // Admin - Collections
  adminCreateCollection: async (collectionData, token) => {
    const response = await axios.post(
      `${API}/admin/collections`,
      collectionData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // Admin - Newsletter
  adminGetNewsletterSubscribers: async (token) => {
    const response = await axios.get(
      `${API}/admin/newsletter`,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // Admin - Contacts
  adminGetContactMessages: async (token) => {
    const response = await axios.get(
      `${API}/admin/contacts`,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // Admin - Coupons
  getCoupons: async () => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.get(
      `${API}/admin/coupons`,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  createCoupon: async (couponData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.post(
      `${API}/admin/coupons`,
      couponData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  updateCoupon: async (couponId, couponData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.put(
      `${API}/admin/coupons/${couponId}`,
      couponData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  deleteCoupon: async (couponId) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.delete(
      `${API}/admin/coupons/${couponId}`,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  validateCoupon: async (code, subtotal) => {
    const response = await axios.post(
      `${API}/validate-coupon?code=${code}&subtotal=${subtotal}`
    );
    return response.data;
  },

  // Settings
  getSettings: async () => {
    const response = await axios.get(`${API}/settings`);
    return response.data;
  },

  updateSettings: async (settingsData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.put(
      `${API}/admin/settings`,
      settingsData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // Affiliates
  registerAffiliate: async (affiliateData) => {
    const response = await axios.post(`${API}/affiliate/register`, affiliateData);
    return response.data;
  },

  validateAffiliateCode: async (code) => {
    const response = await axios.get(`${API}/affiliate/validate/${code}`);
    return response.data;
  },

  getAffiliateStats: async (code) => {
    const response = await axios.get(`${API}/affiliate/stats/${code}`);
    return response.data;
  },

  // Admin - Affiliates
  getAffiliates: async () => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.get(
      `${API}/admin/affiliates`,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  updateAffiliate: async (affiliateId, updateData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.put(
      `${API}/admin/affiliates/${affiliateId}`,
      updateData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  createAffiliatePayout: async (affiliateId, payoutData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.post(
      `${API}/admin/affiliates/${affiliateId}/payout`,
      payoutData,
      { headers: { 'X-Admin-Token': token } }
    );
    return response.data;
  },

  // User Authentication
  registerUser: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  },

  loginUser: async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    return response.data;
  },

  getCurrentUser: async () => {
    const token = localStorage.getItem('user_token');
    const response = await axios.get(
      `${API}/auth/me`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    return response.data;
  },

  // Affiliate Authentication  
  loginAffiliate: async (credentials) => {
    const response = await axios.post(`${API}/affiliate/login`, credentials);
    return response.data;
  },

  getCurrentAffiliate: async () => {
    const token = localStorage.getItem('affiliate_token');
    const response = await axios.get(
      `${API}/affiliate/me`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    return response.data;
  },

  // Page Content Management
  getPageContent: async () => {
    const response = await axios.get(`${API}/page-content`);
    return response.data;
  },

  updatePageContent: async (contentData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.put(
      `${API}/admin/page-content`,
      contentData,
      { headers: { 'Authorization': token } }
    );
    return response.data;
  },

  uploadMedia: async (fileData) => {
    const token = localStorage.getItem('admin_token');
    const response = await axios.post(
      `${API}/admin/upload-media`,
      fileData,
      { headers: { 'Authorization': token } }
    );
    return response.data;
  },
};

// Export named functions for convenience
export const {
  getCollections,
  getCollectionBySlug,
  getProducts,
  getProduct,
  createOrder,
  getOrdersByEmail,
  subscribeNewsletter,
  getPageContent,
  updatePageContent,
  uploadMedia
} = api;

export default api;
